import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import '../index.scss';

import { toastr } from 'react-redux-toastr';
import Header from '../../../components/Header';
import Select from '../../../components/Select';
import NewBotUploadAvatar from './NewBotUploadAvatar';

import {
  fetchVoices,
  fetchVoicebotInstances,
  fetchProjects,
} from '../../../store/actions/voice-bots';
import { BotsCreators } from '../../../store/actions/bots';

import { formatFileToBackend } from './utils';
import history from '../../../routes/history';

const NewVoiceBotCreate = () => {
  const [currentFile, setCurrentFile] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [voiceBotData, setVoiceBotData] = useState({
    title: '',
    codeUser: '',
    instance: null,
    project: null,
    voice: null,
  });

  const { signedUser } = useSelector((state) => state.users);
  const { voices, voicebotInstances, projects } = useSelector(
    (state) => state.voiceBot
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (signedUser) {
      if (signedUser.codeUser) {
        const { codeUser } = signedUser;
        setVoiceBotData((oldState) => ({ ...oldState, codeUser }));
      } else {
        toastr.warning(
          'Aviso',
          'Configure o código de usuário para criação do voicebot'
        );
        history.push(`/preferencias/usuarios`);
      }
    }
  }, [signedUser?.codeUser]);

  useEffect(() => {
    if (signedUser?.selectedOrganization) {
      const organizationId = signedUser.selectedOrganization;
      dispatch(fetchVoicebotInstances({ organizationId }));
    }
  }, [signedUser?.selectedOrganization]);

  useEffect(() => {
    if (!voices?.length) {
      setVoiceBotData((oldState) => ({ ...oldState, voice: null }));
    }
    if (!voicebotInstances?.length) {
      setVoiceBotData((oldState) => ({ ...oldState, instance: null }));
    }
    if (!projects?.length) {
      setVoiceBotData((oldState) => ({ ...oldState, project: null }));
    }
  }, [voices, voicebotInstances, projects]);

  const projectOptions = useMemo(() => {
    return Array.isArray(projects)
      ? projects.map(({ IdProject, DeProject }) => ({
          value: IdProject,
          label: DeProject,
        }))
      : [];
  }, [projects]);

  const voicebotInstanceOptions = useMemo(() => {
    return Array.isArray(voicebotInstances)
      ? voicebotInstances.map(({ _id, name, ...rest }) => ({
          value: _id,
          label: name,
          ...rest,
        }))
      : [];
  }, [voicebotInstances]);

  const voiceOptions = useMemo(() => {
    return Array.isArray(voices)
      ? voices.map(({ IdIvrVoice, DeIvrVoice }) => ({
          value: IdIvrVoice,
          label: DeIvrVoice,
        }))
      : [];
  }, [voices]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setVoiceBotData({ ...voiceBotData, [name]: value });
  };

  const handleChangeInstance = (event) => {
    const { value } = event.target;
    const { codeUser } = voiceBotData;

    if (value && codeUser) {
      const voicebotInstanceId = value.value;
      const params = {
        codeUser,
        voicebotInstanceId,
      };

      dispatch(fetchProjects({ params }));
    }

    handleInputChange(event);
  };

  const handleChangeProject = (event) => {
    const { value } = event.target;
    const { codeUser, instance } = voiceBotData;

    if (value && codeUser && instance) {
      const voicebotInstanceId = instance.value;
      const params = {
        voicebotInstanceId,
        codeUser,
        idProject: value.value,
      };

      dispatch(fetchVoices({ params }));
    }

    handleInputChange(event);
  };

  const validateData = () => {
    const { voice, codeUser, project, instance } = voiceBotData;

    if (!codeUser) {
      toastr.warning('Aviso', 'Configure o código de usuário');
      return false;
    }

    if (!instance) {
      toastr.warning('Aviso', 'Selecione a instância desejada');
      return false;
    }

    if (!project) {
      toastr.warning('Aviso', 'Selecione o projeto');
      return false;
    }

    if (!voice) {
      toastr.warning('Aviso', 'Selecione o tipo de voz que deseja configurar');
      return false;
    }

    return true;
  };

  const handleCreateVoicebot = (event) => {
    event.preventDefault();

    const isValidData = validateData();

    if (!isValidData) return;

    if (signedUser?.selectedOrganization && signedUser?.selectedCompany?._id) {
      const { selectedOrganization: organizationId } = signedUser;
      const companyId = signedUser.selectedCompany._id;
      const { title, codeUser, project, voice, instance } = voiceBotData;

      const payload = {
        title: title || 'Novo Voice Bot',
        description: 'Descrição',
        type: 'voice',
        companyId,
        organizationId,
        codeUser,
        idProject: project?.value,
        idIvrVoice: voice?.value,
        voicebotInstanceId: instance?.value,
        ...(currentFile && {
          file: formatFileToBackend(croppedImage, currentFile),
        }),
      };

      dispatch(BotsCreators.newVoiceBotRequest(payload));
    }
  };

  return (
    <div className="new-bot-create-main">
      <Header pagetype="page-inicio" />
      <div className="new-voice-bot__card ">
        <h2 className="new-voice-bot__title">Novo Voicebot</h2>

        <div className="new-voice-bot__avatar-container">
          <NewBotUploadAvatar
            croppedImage={croppedImage}
            setCroppedImage={setCroppedImage}
            currentFile={currentFile}
            setCurrentFile={setCurrentFile}
          />
        </div>

        <div className="new-voice-bot__container-input">
          <label className="label">Nome do bot</label>
          <input
            name="title"
            placeholder="Ex.: RH"
            value={voiceBotData.title}
            onChange={handleInputChange}
            maxLength={26}
          />
        </div>

        <div className="new-voice-bot__container-input">
          <Select
            label="Instância"
            options={voicebotInstanceOptions}
            onChange={(option) =>
              handleChangeInstance({
                target: { name: 'instance', value: option },
              })
            }
            value={voiceBotData.instance}
            noOptionsMessage="Opção não encontrada"
            getOptionLabel={(option) => `${option.label} - ${option.baseUrl}`}
          />
        </div>

        <div className="new-voice-bot__container-input">
          <Select
            label="Projeto"
            options={projectOptions}
            onChange={(option) =>
              handleChangeProject({
                target: { name: 'project', value: option },
              })
            }
            value={voiceBotData.project}
            noOptionsMessage="Opção não encontrada"
            getOptionLabel={(option) => `Id: ${option.value} - ${option.label}`}
          />
        </div>

        {/* <div className="new-voice-bot__container-input">
          <label className="label">Id do projeto</label>
          <input
            name="idProject"
            placeholder="1273"
            value={voiceBotData.idProject}
            onChange={handleInputChange}
            onBlur={fetchVoicesOnBlur}
          />
        </div> */}

        <div className="new-voice-bot__container-input">
          <Select
            label="Tipo de voz"
            options={voiceOptions}
            onChange={(option) =>
              handleInputChange({ target: { name: 'voice', value: option } })
            }
            value={voiceBotData.voice}
            noOptionsMessage="Opção não encontrada"
          />
        </div>

        <div className="new-voice-bot__footer">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleCreateVoicebot}
          >
            continuar
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewVoiceBotCreate;
